import { ProductVariantPriceRangeFragment } from '@graphql/fragments/__generated__/ProductVariantPriceRange.generated'
import { useCartUpdateProductVariantMutation } from '@graphql/mutations/__generated__/product-reflection.generated'
import {
  CartDocument,
  CartLinesQueryResult,
  useCartLinesLazyQuery,
  useCartQuery,
} from '@graphql/queries/__generated__/cart.generated'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import { Divider } from '@mui/material'
import Button from '@mui/material/Button'
import PointsSvg from 'images/Points.svg'
import React, { useEffect, useMemo, useState } from 'react'
import Loader from '@components/Loader'
import { Link } from 'react-router-dom'
import { useQueryParams } from 'use-query-params'
import { ACCOUNT_CREATE_URL, CART_URL, getProductUrl, WHOLESALE_CART_URL } from '../../../urls'
import { GlobalDialogFormsParams } from '../../GlobalDialogForms'
import ProductDetailsDialog from '../../Product/ProductDetailsDialog'
import { CartTypeEnum } from '../../../../generated/gqlTypes'
import { useReactiveVar } from '@apollo/client'
import { isWholesaleModeVar } from '../../../../apolloClient'
import FormattedPrice from '@components/FormattedPrice'
import { Trans, useTranslation } from 'react-i18next'

const SHOW_ITEMS = 3

interface CartLinePriceProps {
  line: CartLinesQueryResult['data']['cartLines']['results'][0]
  qty: number
}

const CartLinePrice: React.FC<CartLinePriceProps> = ({ line, qty }) => {
  const priceRange: ProductVariantPriceRangeFragment = useMemo(
    () =>
      !line.supplyParts.length
        ? line.variant.priceRange
        : line.supplyParts.reduce(
            (prev, curr) => {
              return {
                currency: prev.currency,
                discount: {
                  min: prev.discount.min + curr.variant.priceRange.discount.min * curr.quantity,
                  max: prev.discount.max + curr.variant.priceRange.discount.max * curr.quantity,
                },
                undiscount: {
                  min: prev.undiscount.min + curr.variant.priceRange.undiscount.min * curr.quantity,
                  max: prev.undiscount.max + curr.variant.priceRange.undiscount.max * curr.quantity,
                },
              } as ProductVariantPriceRangeFragment
            },
            {
              currency: line.variant.priceRange.currency,
              discount: {
                min: 0,
                max: 0,
              },
              undiscount: {
                min: 0,
                max: 0,
              },
            } as ProductVariantPriceRangeFragment
          ),
    [line]
  )
  const isPriceRange = priceRange.discount.max !== priceRange.discount.min
  const isSale =
    priceRange.discount.max !== priceRange.undiscount.max || priceRange.discount.min !== priceRange.undiscount.min
  return (
    <>
      {isPriceRange ? (
        <span>
          <span className={isSale ? 'line-through' : ''}>
            <FormattedPrice currency={priceRange.currency} amount={priceRange.undiscount.min * qty} />
          </span>

          <span> - </span>
          <span className={isSale ? 'line-through' : ''}>
            <FormattedPrice currency={priceRange.currency} amount={priceRange.undiscount.max * qty} />
          </span>
        </span>
      ) : (
        <span className={isSale ? 'line-through' : ''}>
          <FormattedPrice currency={priceRange.currency} amount={priceRange.undiscount.min * qty} />
        </span>
      )}
      {isSale &&
        (isPriceRange ? (
          <span className='text-red'>
            <span>
              <FormattedPrice currency={priceRange.currency} amount={priceRange.discount.min * qty} />
            </span>

            <span> - </span>
            <span>
              <FormattedPrice currency={priceRange.currency} amount={priceRange.discount.max * qty} />
            </span>
          </span>
        ) : (
          <span className='text-red'>
            {' '}
            <FormattedPrice currency={priceRange.currency} amount={priceRange.discount.min * qty} />
          </span>
        ))}
    </>
  )
}

interface CartLineWholesalePriceProps {
  line: CartLinesQueryResult['data']['cartLines']['results'][0]
  qty: number
}

const CartLineWholesalePrice: React.FC<CartLineWholesalePriceProps> = ({ line, qty }) => {
  const linePrice = useMemo<{ amount: number; currency: string } | null>(() => {
    if (line) {
      if (!line.supplyParts.length) {
        return {
          currency: line.variant.wholesalePrice.currency,
          amount: line.variant.wholesalePrice.undiscount,
        }
      } else {
        return line.supplyParts.reduce(
          (prev, curr) => {
            return {
              currency: curr.variant.wholesalePrice.currency,
              amount: prev.amount + curr.variant.wholesalePrice.undiscount * curr.quantity,
            }
          },
          { currency: '', amount: 0 }
        )
      }
    }
    return null
  }, [line])

  if (!linePrice) return null

  return (
    <span>
      <FormattedPrice amount={linePrice.amount * qty} currency={linePrice.currency} />
    </span>
  )
}

interface HeaderCartProps {
  isHover: boolean
}

const HeaderCart: React.FC<HeaderCartProps> = ({ isHover }) => {
  const { t } = useTranslation()
  const [, setParams] = useQueryParams(GlobalDialogFormsParams)
  const [loadingLines, setLoadingLines] = useState([])
  const [activeProductData, setActiveProductData] = useState(null)
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const meQueryResult = useMeQuery()
  const isAuthenticated = !!meQueryResult.data?.me
  const cartType = isWholesaleMode ? CartTypeEnum.Wholesale : CartTypeEnum.Default
  const cartQueryResult = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType },
  })
  const [loadCartLines, cartLinesQueryResult] = useCartLinesLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      cartType,
      limit: SHOW_ITEMS,
      offset: 0,
      supplyIsNull: true,
    },
  })
  useEffect(() => {
    if (isHover && !cartLinesQueryResult.data) {
      loadCartLines()
    }
  }, [isHover, cartLinesQueryResult.data])

  const [cartUpdateVariant] = useCartUpdateProductVariantMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'CartLines',
      {
        query: CartDocument,
        variables: { cartType },
      },
    ],
  })
  const itemSubtotal = cartQueryResult.data?.cart.totalPrice.discount.gross
  const currency = cartQueryResult.data?.cart.totalPrice.discount.currency || 'USD'
  const rewards = meQueryResult.data?.me?.totalBalance?.gross || 0
  const creditsApplied = rewards > itemSubtotal ? itemSubtotal : rewards
  const earnPoints = useMemo(
    () =>
      Math.max(
        cartQueryResult.data?.cart
          ? Math.round((cartQueryResult.data?.cart?.totalPrice.earnPoints - creditsApplied) * 100) / 100
          : 0,
        0
      ),
    [cartQueryResult.data, creditsApplied]
  )

  const handleRemoveLine = (linePk: number) => async () => {
    setLoadingLines(prev => [...prev, linePk])
    try {
      const lines = cartLinesQueryResult.data.cartLines.results
      const variant = lines.find(line => line.pk === linePk)?.variant
      await cartUpdateVariant({
        variables: {
          cartLine: linePk,
          quantity: 0,
          variant: variant.pk,
          cartType: isWholesaleMode ? CartTypeEnum.Wholesale : CartTypeEnum.Default,
        },
      })
    } catch (e) {
      console.error(e.message)
    }
    setLoadingLines(prev => prev.filter(pk => pk !== linePk))
  }
  const handleClickProduct = spec => e => {
    e.preventDefault()
    setActiveProductData({ pk: spec.pk, slug: spec.slug })
  }

  const itemsCount = cartLinesQueryResult.data?.cartLines?.totalCount - SHOW_ITEMS
  return (
    <>
      <div>
        {cartLinesQueryResult.loading && (
          <Loader className='spinner spinner--small' type='ThreeDots' color='#333' visible height={100} width={100} />
        )}
        <ul className='p-0 m-0 list-none overflow-auto text-sm'>
          {cartLinesQueryResult.data?.cartLines.results.map(line => {
            const variant = line.variant
            const product = variant.product
            const spec = product.siteSpecifics[0]
            return (
              <li key={line.id} className='p-12 flex w-full border-0 border-b border-gray-400 border-solid relative'>
                <Loader
                  className='spinner spinner--small'
                  type='ThreeDots'
                  color='#333'
                  visible={loadingLines.includes(line.pk)}
                  height={100}
                  width={100}
                />
                <Link to={getProductUrl(spec.slug, spec.pk)} onClick={handleClickProduct(spec)}>
                  <img
                    className='w-90 mr-10 max-w-full'
                    src={variant.thumbnailUrl || spec.thumbnailUrl}
                    alt={spec.name}
                  />
                </Link>
                <div className='flex-1'>
                  <div>
                    <Link
                      className='no-underline hover:underline line-clamp-2 pb-4'
                      to={getProductUrl(spec.slug, spec.pk)}
                      onClick={handleClickProduct(spec)}
                    >
                      {spec.name}
                    </Link>
                  </div>
                  <div className='font-bold'>
                    {isWholesaleMode ? (
                      <CartLineWholesalePrice qty={line.quantity} line={line} />
                    ) : (
                      <CartLinePrice qty={line.quantity} line={line} />
                    )}
                  </div>
                  {variant.properties.map((prop, idx) => (
                    <div key={idx}>
                      <span>{prop.attributeName}: </span>
                      <span>{prop.valueName}</span>
                    </div>
                  ))}
                  <div>
                    {t('quantity', 'Quantity')}: {line.quantity}
                  </div>
                  <div className='-ml-10'>
                    <Button
                      className='transparent'
                      type='button'
                      size='small'
                      onClick={handleRemoveLine(line.pk)}
                      disableRipple
                      disableElevation
                    >
                      <span className='underline text-12 normal-case'>{t('button.remove', 'Remove')}</span>
                    </Button>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
        {itemsCount > 0 && (
          <>
            <div className='p-12 flex items-center font-bold justify-center text-center'>
              <span>
                <Trans i18nKey='header.cartItemsCountDescription' count={itemsCount}>
                  There are {{ itemsCount }} more items in your
                  <Link to={isWholesaleMode ? WHOLESALE_CART_URL : CART_URL} className='underline'>
                    shopping bag
                  </Link>
                </Trans>
              </span>
            </div>
            <Divider />
          </>
        )}

        <div className='p-12 pt-24 text-center font-bold text-base'>
          {t('subtotal', 'Subtotal')}: <FormattedPrice currency={currency} amount={itemSubtotal} />
        </div>
        <div className='px-12 pb-12'>
          <Button
            component={Link}
            to={isWholesaleMode ? WHOLESALE_CART_URL : CART_URL}
            variant='contained'
            color='secondary'
            size='large'
            fullWidth
            disableElevation
          >
            <span className='font-bold tracking-1'>{t('button.shoppingBag', 'Shopping Bag')}</span>
          </Button>
        </div>
        {!!earnPoints && !isWholesaleMode && (
          <div className='p-12 pt-0'>
            <div className='flex items-center text-13'>
              <div className='w-40 mr-10'>
                <img src={PointsSvg} alt='Points' className='max-w-full' />
              </div>
              <div className='flex-1'>
                {!isAuthenticated && (
                  <span className='text-gray-800'>
                    <Trans
                      i18nKey='signInToEarnPoints'
                      defaults='<a>Sign in</a> to earn <strong>{{earnPoints}}</strong> points with this purchase!'
                      values={{ earnPoints: earnPoints }}
                      count={earnPoints}
                      components={{
                        a: (
                          <Link
                            className='text-blue no-underline'
                            to={ACCOUNT_CREATE_URL}
                            onClick={e => {
                              e.preventDefault()
                              setParams({ global_dialog_form: 'signIn' }, 'replaceIn')
                            }}
                          />
                        ),
                        strong: <strong className='text-black' />,
                      }}
                    />
                  </span>
                )}
                {isAuthenticated && (
                  <span className='text-gray-800'>
                    <Trans
                      i18nKey='youEarnPointsWithPurchase'
                      defaults={`Good news! You'll earn <strong>{{earnPoints}}</strong> points with this purchase!`}
                      values={{ earnPoints: earnPoints }}
                      count={earnPoints}
                      components={{
                        strong: <strong className='relative text-black' />,
                      }}
                    />
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <ProductDetailsDialog isOpen={!!activeProductData} setIsOpen={setActiveProductData} spec={activeProductData} />
    </>
  )
}

export default HeaderCart
